import React from 'react';
import { cssMap } from '@atlaskit/css';
import { SkeletonItem } from '@atlaskit/menu';
import { Box as BoxOld, Inline as InlineOld, xcss } from '@atlaskit/primitives';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { fg } from '@atlassian/jira-feature-gating';
import type { PopupHeight, PopupWidth } from '../../../../types.tsx';
import { Layout } from '../layout/index.tsx';
import { ValuesFallbackSkeleton } from '../values-panel/fallback-skeleton/index.tsx';

const POPUP_HEIGHT: PopupHeight = '380px';
const POPUP_WIDTH: PopupWidth = '600px';

const styles = cssMap({
	buttonPadding: {
		paddingInline: token('space.150'),
	},
});

export const FallbackSkeleton = () => {
	const showImprovedStyling =
		fg('list_lovability_improving_filters') || fg('filter_refinement_m2_date_fields');

	if (!showImprovedStyling) {
		return (
			<UFOLoadHold name="filter-popup-fallback">
				<BoxOld xcss={fallbackContentStyles}>
					<InlineOld xcss={FullHeightStyle}>
						<BoxOld xcss={leftBoxStyles}>
							<BoxOld xcss={flexGrowStyles}>
								{Array.from({ length: 4 }, (_, index) => (
									<SkeletonItem isShimmering key={index} width="120px" />
								))}
							</BoxOld>
							<BoxOld xcss={clearAllStyles}>
								<Skeleton isShimmering width="65px" height="9px" />
							</BoxOld>
						</BoxOld>
						<BoxOld xcss={rightBoxStyles}>
							<ValuesFallbackSkeleton />
						</BoxOld>
					</InlineOld>
				</BoxOld>
			</UFOLoadHold>
		);
	}

	return (
		<UFOLoadHold name="filter-popup-fallback">
			<Layout
				leftContent={
					<Stack grow="fill" spread="space-between">
						<Box>
							{Array.from({ length: 4 }, (_, index) => (
								<SkeletonItem isShimmering key={index} width="120px" xcss={styles.buttonPadding} />
							))}
						</Box>
						<Box xcss={styles.buttonPadding}>
							<Skeleton isShimmering width="65px" height="9px" />
						</Box>
					</Stack>
				}
				rightContent={<ValuesFallbackSkeleton />}
				footer={null}
			/>
		</UFOLoadHold>
	);
};

const flexGrowStyles = xcss({
	flex: 1,
});

const clearAllStyles = xcss({
	paddingLeft: 'space.300',
	paddingBottom: 'space.100',
});

const fallbackContentStyles = xcss({
	paddingBlock: 'space.250',
	// keep height and width the same as popup content
	height: POPUP_HEIGHT,
	width: POPUP_WIDTH,
});

const leftBoxStyles = xcss({
	paddingInline: 'space.250',
	width: '240px',
	display: 'flex',
	flexDirection: 'column',
});

const rightBoxStyles = xcss({
	paddingInline: 'space.250',
	width: '360px',
	display: 'flex',
	flexDirection: 'column',
	borderLeft: `1px solid ${token('color.border')}`,
});

const FullHeightStyle = xcss({
	// the grey border line right needs to fill full height
	alignItems: 'stretch',
	height: '100%',
});
